import { useState } from 'react';

/**
 * حقلُ هاتفٍ بمِفتاح الدولة وعلمِها.
 *
 * تُختار الدولةُ من قائمةٍ بأعلامها ومفاتيحها ثمّ يُكتب الرقمُ المحلّيّ،
 * ويُحفظ الكلُّ نصًّا واحدًا «+<مفتاح><رقم>». وعند الفتح يُفكَّك النصُّ
 * القائمُ إلى دولةٍ ورقمٍ بمطابقة أطول مفتاحٍ يبدأ به.
 */
interface Country {
  iso: string;
  name: string;
  dial: string;
  flag: string;
}

/* قائمةٌ تبدأ بالخليج ومصر ثمّ اتّساعٌ معقول. */
const COUNTRIES: Country[] = [
  { iso: 'EG', name: 'مصر', dial: '20', flag: '🇪🇬' },
  { iso: 'SA', name: 'السعودية', dial: '966', flag: '🇸🇦' },
  { iso: 'AE', name: 'الإمارات', dial: '971', flag: '🇦🇪' },
  { iso: 'KW', name: 'الكويت', dial: '965', flag: '🇰🇼' },
  { iso: 'QA', name: 'قطر', dial: '974', flag: '🇶🇦' },
  { iso: 'BH', name: 'البحرين', dial: '973', flag: '🇧🇭' },
  { iso: 'OM', name: 'عُمان', dial: '968', flag: '🇴🇲' },
  { iso: 'JO', name: 'الأردن', dial: '962', flag: '🇯🇴' },
  { iso: 'LB', name: 'لبنان', dial: '961', flag: '🇱🇧' },
  { iso: 'PS', name: 'فلسطين', dial: '970', flag: '🇵🇸' },
  { iso: 'SY', name: 'سوريا', dial: '963', flag: '🇸🇾' },
  { iso: 'IQ', name: 'العراق', dial: '964', flag: '🇮🇶' },
  { iso: 'YE', name: 'اليمن', dial: '967', flag: '🇾🇪' },
  { iso: 'SD', name: 'السودان', dial: '249', flag: '🇸🇩' },
  { iso: 'LY', name: 'ليبيا', dial: '218', flag: '🇱🇾' },
  { iso: 'TN', name: 'تونس', dial: '216', flag: '🇹🇳' },
  { iso: 'DZ', name: 'الجزائر', dial: '213', flag: '🇩🇿' },
  { iso: 'MA', name: 'المغرب', dial: '212', flag: '🇲🇦' },
  { iso: 'TR', name: 'تركيا', dial: '90', flag: '🇹🇷' },
  { iso: 'GB', name: 'بريطانيا', dial: '44', flag: '🇬🇧' },
  { iso: 'US', name: 'الولايات المتحدة', dial: '1', flag: '🇺🇸' },
  { iso: 'FR', name: 'فرنسا', dial: '33', flag: '🇫🇷' },
  { iso: 'DE', name: 'ألمانيا', dial: '49', flag: '🇩🇪' },
  { iso: 'IT', name: 'إيطاليا', dial: '39', flag: '🇮🇹' },
  { iso: 'ES', name: 'إسبانيا', dial: '34', flag: '🇪🇸' },
  { iso: 'CA', name: 'كندا', dial: '1', flag: '🇨🇦' },
  { iso: 'IN', name: 'الهند', dial: '91', flag: '🇮🇳' },
  { iso: 'PK', name: 'باكستان', dial: '92', flag: '🇵🇰' },
];

/* أطولُ مفتاحٍ أولًا حتى لا يُطابَق «1» قبل «971». */
const BY_DIAL_DESC = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);

function parsePhone(value: string): { iso: string; local: string } {
  const raw = (value ?? '').trim();
  if (raw.startsWith('+')) {
    const digits = raw.slice(1).replace(/[^\d]/g, '');
    const hit = BY_DIAL_DESC.find((c) => digits.startsWith(c.dial));
    if (hit) return { iso: hit.iso, local: digits.slice(hit.dial.length) };
  }
  /* بلا مفتاحٍ صريح: يُعدّ رقمًا محلّيًّا مصريًّا. */
  return { iso: 'EG', local: raw.replace(/[^\d]/g, '') };
}

export function PhoneField({
  value,
  onChange,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
}) {
  const parsed = parsePhone(value);
  const [iso, setIso] = useState(parsed.iso);
  const [local, setLocal] = useState(parsed.local);

  const country = COUNTRIES.find((c) => c.iso === iso) ?? COUNTRIES[0];

  const push = (nextIso: string, nextLocal: string) => {
    setIso(nextIso);
    setLocal(nextLocal);
    const c = COUNTRIES.find((x) => x.iso === nextIso) ?? COUNTRIES[0];
    const clean = nextLocal.replace(/[^\d]/g, '');
    onChange(clean ? `+${c.dial}${clean}` : '');
  };

  return (
    <div className="phone-field">
      <select
        className="input phone-cc"
        value={iso}
        onChange={(e) => push(e.target.value, local)}
        aria-label="مفتاح الدولة"
      >
        {COUNTRIES.map((c) => (
          <option key={c.iso} value={c.iso}>
            {c.name} (+{c.dial})
          </option>
        ))}
      </select>
      <span className="phone-dial num" aria-hidden="true">
        <img
          className="phone-flag"
          src={`https://flagcdn.com/${country.iso.toLowerCase()}.svg`}
          alt=""
          loading="lazy"
        />
        +{country.dial}
      </span>
      <input
        className="input num phone-num"
        dir="ltr"
        inputMode="tel"
        value={local}
        placeholder="10 1234 5678"
        maxLength={15}
        onChange={(e) => push(iso, e.target.value)}
        autoFocus={autoFocus}
      />
    </div>
  );
}
